// The β button and the feedback sheet (internal beta). Rendered in their own layer outside #app (see mountBeta), so
// the screenshot of #app never shows them. Phone: a small tab on the left edge, drag it up or down to move it.
// Desktop: a square in the bottom-left corner, and Ctrl/Cmd+Shift+B anywhere. The Working-together screen hides the
// tab while its controls are hidden or it is filming (setBetaQuiet).
import { render } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { FeedbackImage, FeedbackRequest } from '@shared/types';
import { createStore, useStore } from '../lib/createStore';
import { BottomSheet, Icon, PrimaryButton, Waveform, fmtMs, useHoldToTalk } from '../components';
import { useApp, appStore, toast } from '../store';
import { useLayout } from '../layout';
import { isMiniWindow, shellInfo } from '../native';
import { api, ApiError } from '../api/client';
import { safeStorage } from '../api/token';
import { draftStore } from '../screens/onboarding/draft';
import { captureApp, dataUrl, readPicture } from './capture';
import { CHAT_MESSAGES, canAttachChat, chatHasCrisis, collectContext } from './context';
import { sendFeedback } from './send';
import s from './Beta.module.css';

export const betaStore = createStore({ open: false, quiet: false });
export const openFeedback = () => { if (!betaStore.get().open) betaStore.set({ open: true }); };
const closeFeedback = () => betaStore.set({ open: false });
/** Hide the β tab for a while (Working together with its controls hidden, filming mode). */
export const setBetaQuiet = (quiet: boolean) => { if (betaStore.get().quiet !== quiet) betaStore.set({ quiet }); };

const Y_KEY = 'xm.beta.y';
const MAX_PHOTOS = 2;
/** What the tester typed survives closing the sheet by accident (until it is sent). */
const draft = { text: '', voice: false };
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const SHORTCUT = isMac ? '⌘⇧B' : 'Ctrl+Shift+B';

function BetaLayer() {
  const layout = useLayout();
  const booted = useApp((x) => x.booted);
  const open = useStore(betaStore, (x) => x.open);
  const quiet = useStore(betaStore, (x) => x.quiet);
  if (!booted || isMiniWindow()) return null;
  const desk = layout === 'desktop';
  return (
    <>
      {!open && !quiet && (desk ? <DeskButton /> : <EdgeTab />)}
      {open && <FeedbackSheet desk={desk} />}
    </>
  );
}

function DeskButton() {
  return (
    <button type="button" class={s.desk} onClick={openFeedback} aria-label={`Something off? Tell us (${SHORTCUT})`} title={`Something off? Tell us · ${SHORTCUT}`}>
      β
    </button>
  );
}

function EdgeTab() {
  const [y, setY] = useState(() => {
    const v = Number(safeStorage.get('local', Y_KEY));
    // Default: the height where the fewest buttons touch the left edge across the screens (checked 11-02).
    return v > 0.08 && v < 0.92 ? v : 0.42;
  });
  const g = useRef({ id: -1, y0: 0, top0: 0, moved: false });
  const [dragging, setDragging] = useState(false);
  const clampY = (v: number) => Math.min(0.88, Math.max(0.12, v));
  return (
    <button
      type="button" class={s.tab} style={{ top: `${y * 100}%` }} data-drag={dragging ? 'true' : 'false'}
      aria-label="Something off? Tell us" title="Something off? Tell us. Drag to move."
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        g.current = { id: e.pointerId, y0: e.clientY, top0: y * window.innerHeight, moved: false };
      }}
      onPointerMove={(e) => {
        if (g.current.id !== e.pointerId) return;
        const dy = e.clientY - g.current.y0;
        if (!g.current.moved && Math.abs(dy) > 6) { g.current.moved = true; setDragging(true); }
        if (g.current.moved) setY(clampY((g.current.top0 + dy) / window.innerHeight));
      }}
      onPointerUp={(e) => {
        if (g.current.id !== e.pointerId) return;
        const moved = g.current.moved;
        g.current.id = -1; setDragging(false);
        if (moved) safeStorage.set('local', Y_KEY, String(clampY((g.current.top0 + e.clientY - g.current.y0) / window.innerHeight)));
        else openFeedback();
      }}
      onPointerCancel={() => { g.current.id = -1; setDragging(false); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFeedback(); } }}
    >
      <span aria-hidden="true">β</span>
    </button>
  );
}

function Thumb({ img, label, onRemove }: { img: FeedbackImage | 'pending' | null; label: string; onRemove?: () => void }) {
  const [wide, setWide] = useState(false);
  return (
    <figure class={s.thumb} data-state={img === 'pending' ? 'pending' : img ? 'ok' : 'none'} data-wide={wide ? 'true' : 'false'}>
      {img && img !== 'pending'
        ? <img src={dataUrl(img)} alt={label} onLoad={(e) => { const i = e.currentTarget as HTMLImageElement; setWide(i.naturalWidth > i.naturalHeight); }} />
        : <span>{img === 'pending' ? 'Taking a picture…' : 'No picture'}</span>}
      <figcaption>{label}</figcaption>
      {onRemove && img && img !== 'pending' && (
        <button type="button" class={s.remove} onClick={onRemove} aria-label={`Remove ${label.toLowerCase()}`}><Icon name="close" size={14} /></button>
      )}
    </figure>
  );
}

function FeedbackSheet({ desk }: { desk: boolean }) {
  const signedIn = useApp((x) => !!x.token);
  // Everything the app knew when the β was tapped (the sheet itself never adds to the trail).
  const snap = useMemo(() => collectContext({ includeChat: true, shell: null }), []);
  const capture = useMemo(() => captureApp(), []);
  const [shot, setShot] = useState<FeedbackImage | null | 'pending'>('pending');
  const [keepShot, setKeepShot] = useState(true);
  const [photos, setPhotos] = useState<FeedbackImage[]>([]);
  const [text, setTextState] = useState(draft.text);
  const [voice, setVoice] = useState(draft.voice);
  const [chat, setChat] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const attachable = signedIn && canAttachChat();
  const crisis = signedIn && chatHasCrisis();

  useEffect(() => { let live = true; void capture.then((r) => { if (live) setShot(r.shot); }); return () => { live = false; }; }, []);
  const setText = (v: string) => { draft.text = v; setTextState(v); };
  const markVoice = () => { draft.voice = true; setVoice(true); };

  const talk = useHoldToTalk({
    tap: 'toggle',
    onAudio: async (clip) => {
      setError(null);
      try {
        const audio = { b64: clip.b64, mime: clip.mime };
        const r = appStore.get().token ? await api.stt(audio, 'feedback') : await api.sttPublic({ invite: draftStore.get().invite, audio, hint: 'feedback' });
        const said = (r.text || '').trim();
        if (!said) { setError("Didn't catch that. Try again, or type it."); return; }
        setText((draft.text ? draft.text.trimEnd() + ' ' : '') + said);
        markVoice();
      } catch (e) {
        setError(e instanceof ApiError && !e.offline && e.status !== 403 ? e.message : "Couldn't turn that into text here. Type it instead.");
      }
    },
    onError: (e) => setError(e.message || 'Could not start the microphone. Type it instead.'),
    onTooShort: () => setError('Hold a little longer while you talk.'),
  });
  const rec = talk.phase === 'recording' || talk.phase === 'cancel' || talk.phase === 'starting';
  const talkLabel = talk.phase === 'starting' ? 'Listening…'
    : talk.phase === 'recording' ? (talk.toggled ? 'Tap to stop' : 'Let go to stop')
    : talk.phase === 'cancel' ? 'Let go to cancel'
    : talk.phase === 'sending' ? 'Writing it down…'
    : 'Hold to talk';

  const addPicture = async (f: File | undefined) => {
    if (!f) return;
    try { const img = await readPicture(f); setPhotos((p) => [...p, img].slice(0, MAX_PHOTOS)); } catch { setError("That picture didn't open. Try another one."); }
  };

  const send = async () => {
    if (busy) return;
    const words = text.trim();
    if (!words && !photos.length) { setError('Say or type a few words first.'); area.current?.focus(); return; }
    setBusy(true); setError(null);
    const cap = await capture;
    const shell = await shellInfo();
    const context: Record<string, unknown> = { ...snap, shell: shell ? shell.version + (shell.build ? ` (${shell.build})` : '') : null, sentAt: new Date().toISOString(), shotMs: cap.ms };
    if (cap.error) context.shotError = cap.error;
    if (!keepShot) context.shotRemoved = true;
    if (signedIn && !(chat && attachable)) {
      context.messages = null;
      context.messagesOmitted = crisis ? 'crisis' : attachable ? 'tester chose not to attach' : 'no chat yet';
    }
    const req: FeedbackRequest = {
      text: words, voice, shot: keepShot ? cap.shot : null, photos, context,
      ...(signedIn ? {} : { invite: draftStore.get().invite || undefined }),
    };
    const r = await sendFeedback(req);
    setBusy(false);
    if (!r.ok && !r.queued) { setError(r.message); return; }
    draft.text = ''; draft.voice = false;
    closeFeedback();
    toast(r.ok ? 'Sent. Thank you!' : "Saved. It'll send when you're back online.");
  };

  const close = () => { talk.cancel(); closeFeedback(); };
  const body = (
    <div class={s.body}>
      <div class={s.head}>
        <h2 class={s.title}>Something off?</h2>
        <button type="button" class={s.x} onClick={close} aria-label="Close"><Icon name="close" size={18} /></button>
      </div>
      <p class={s.sub}>Tell us what happened. A picture of this screen and your last few taps come with it.{desk ? ` Shortcut: ${SHORTCUT}.` : ''}</p>
      <div class={s.pics}>
        {keepShot && <Thumb img={shot} label="This screen" onRemove={() => setKeepShot(false)} />}
        {photos.map((p, i) => <Thumb key={i} img={p} label="Your picture" onRemove={() => setPhotos((x) => x.filter((_, j) => j !== i))} />)}
        {photos.length < MAX_PHOTOS && (
          <button type="button" class={s.add} onClick={() => file.current?.click()}>
            <Icon name="plus" size={18} /><span>Add picture</span>
          </button>
        )}
        <input ref={file} type="file" accept="image/*" hidden onChange={(e) => { const f = (e.currentTarget as HTMLInputElement).files?.[0]; (e.currentTarget as HTMLInputElement).value = ''; void addPicture(f); }} />
      </div>
      <label class={s.label} for="xm-fb-text">What happened?</label>
      <textarea
        id="xm-fb-text" ref={area} class={s.text} rows={3} maxLength={4000} value={text}
        placeholder="What did you do, and what did you expect?"
        onInput={(e) => setText((e.currentTarget as HTMLTextAreaElement).value)}
      />
      <div class={s.actions}>
        <button
          type="button" class={s.mic} data-live={rec ? 'true' : 'false'} {...talk.bind}
          aria-label={rec ? 'Stop' : 'Hold to talk, or tap to start and tap again to stop'}
        >
          {rec ? <Waveform levels={talk.levels} bars={10} height={18} /> : <Icon name="mic" size={18} />}
          <span>{talkLabel}{talk.phase === 'recording' ? ` · ${fmtMs(talk.ms)}` : ''}</span>
        </button>
        <PrimaryButton h={48} onClick={send} busy={busy} disabled={busy || rec || talk.phase === 'sending'} style={{ flex: '1 1 0', minWidth: 110 }}>
          {busy ? 'Sending…' : 'Send'}
        </PrimaryButton>
      </div>
      {attachable && (
        <label class={s.check}>
          <input type="checkbox" checked={chat} onChange={(e) => setChat((e.currentTarget as HTMLInputElement).checked)} />
          <span>Add my last {CHAT_MESSAGES} messages</span>
        </label>
      )}
      {crisis && <p class={s.note}>Your recent chat stays out of this report.</p>}
      {error && <p class={s.error} role="alert">{error}</p>}
    </div>
  );
  return (
    <div class={s.layer} data-beta="">
      <BottomSheet
        open onClose={close} label="Something off? Tell us" scrim={desk ? 0.35 : 0.55}
        style={desk ? { left: 24, right: 'auto', bottom: 24, width: 440, maxHeight: 'calc(100% - 48px)', borderRadius: 24, padding: 22, border: '1px solid rgba(255,255,255,0.14)' } : undefined}
      >
        {body}
      </BottomSheet>
    </div>
  );
}

/** Puts the β layer on the page, outside #app. Call once after the app renders. */
export function mountBeta(): void {
  if (document.getElementById('xm-beta')) return;
  const host = document.createElement('div');
  host.id = 'xm-beta';
  host.setAttribute('data-beta', '');
  host.className = s.host;
  document.body.appendChild(host);
  render(<BetaLayer />, host);
}
